import { gql } from "@apollo/client";
import { z } from "zod";
import { partnerLoginId } from "../../redux/constants";

export const GET_STATES_BY_COUNTRY_ID = gql`
  query ($country_id: Int!) {
    allStatesByCountryId(country_id: $country_id) {
      country_id
      name
      zone_id
    }
  }
`;

export const GET_ALL_ALERTS_BY_ALERT_TYPE = gql`
  query ($clientId: Int!, $alertType: String!, $from: String, $to: String) {
    getAllAlertsByAlertType(
      clientLogin: $clientId
      alertType: $alertType
      from: $from
      to: $to
    ) {
      uniqueid
      alerttype
      alertvalue
      from_ts
      to_ts
      address
      view_status
      lat
      lng
      __typename
    }
  }
`;
export const GET_ALL_ALERTS_BY_CLIENT_LOGIN = gql`
  query getAllAlertsByClientLogin($clientId: Int!, $from: String, $to: String) {
    alerts: getAllAlertsByClientLogin(
      clientLogin: $clientId
      from: $from
      to: $to
    ) {
      alerttype
      view_status
      __typename
    }
  }
`;

export const GET_COUNTRYS = gql`
  query {
    allCountries {
      country_id
      name
      iso_code_2
    }
  }
`;
export const GET_ALL_VEHICLE_MODEL = gql`
  query {
    getAllVehicleModel {
      id
      name
    }
  }
`;
export const GET_ALL_SPECIFIC_TASKT = gql`
  query {
    getAllSpecificTasks {
      id
      name
    }
  }
`;
export const GET_ALL_DOMAIN = gql`
  query {
    getAllDomainBusinesses {
      id
      name
    }
  }
`;
export const ALL_PARTNER_DETAILS = gql`
  query {
    allPartnerDetails(status: 1) {
      id
      businessName
      totalRegisteredVehicles
    }
  }
`;

export const schema = z
  .object({
    name: z.string().min(5, "Name is required"),
    email: z.string().email("Invalid email"),
    phone: z
      .string()
      .min(8, "phone must be at least 8 characters")
      .regex(/^(\+|\d)(\d+)$/, "Invalid phone number"),
    password: z.string().min(9, "Password must be at least 9 characters"),
    confirmPassword: z.string(),
    type: z.enum(["PA", "ID", "CLT"]),
    company: z.string().optional(),
    step: z.number(),
    clientType: z.string().optional(),
    country: z
      .union([z.string(), z.number()])
      .optional()
      .transform((val) => (val === "" ? undefined : Number(val))),
    planId: z
      .union([z.string(), z.number()])
      .optional()
      .transform((val) => (val === "" ? undefined : Number(val))),
    city: z.string().optional(),
    state: z
      .union([z.string(), z.number()])
      .optional()
      .transform((val) => (val === "" ? undefined : Number(val))),
    address: z.string().optional(),
    task: z
      .union([z.string(), z.number()])
      .optional()
      .transform((val) => (val === "" ? undefined : Number(val))), // pour gérer les input texte
    comment: z.string().optional(),
    domain: z
      .union([z.string(), z.number()])
      .optional()
      .transform((val) => (val === "" ? undefined : Number(val))), // pour gérer les input texte
    vehicleCount: z
      .union([z.string(), z.number()])
      .optional()
      .transform((val) => (val === "" ? undefined : Number(val))), // pour gérer les input texte
  })
  .superRefine((data, ctx) => {
    if (data.type === "company") {
      ["task", "domain", "state", "country"].forEach((field) => {
        if (data[field] === undefined || data[field] === "") {
          ctx.addIssue({
            path: [`${field}`],
            message: `${field} is required`,
            code: z.ZodIssueCode.custom,
          });
        }
      });
      if (!data.company || data.company.trim().length < 2) {
        ctx.addIssue({
          path: ["company"],
          message: "Company name is required and must be at least 2 characters",
          code: z.ZodIssueCode.custom,
        });
      }
      if (!data.country || data.country <= 0) {
        ctx.addIssue({
          path: ["country"],
          message: "country name is required and must be at least 2 characters",
          code: z.ZodIssueCode.custom,
        });
      }
      if (data.step === 1) {
        if (!data.task || data.task <= 0) {
          ctx.addIssue({
            path: ["task"],
            message: "Task name is required and must be at least 2 characters",
            code: z.ZodIssueCode.custom,
          });
        }
        if (!data.domain || data.domain <= 0) {
          ctx.addIssue({
            path: ["domain"],
            message:
              "Domain name is required and must be at least 2 characters",
            code: z.ZodIssueCode.custom,
          });
        }
        if (!data.comment || data.comment.trim().length < 2) {
          ctx.addIssue({
            path: ["comment"],
            message: "Comment is required and must be at least 2 characters",
            code: z.ZodIssueCode.custom,
          });
        }
      }

      if (
        data.vehicleCount === undefined ||
        isNaN(data.vehicleCount) ||
        data.vehicleCount < 1
      ) {
        ctx.addIssue({
          path: ["vehicleCount"],
          message: "Le nombre de véhicules doit être au moins 1",
          code: z.ZodIssueCode.custom,
        });
      }
    }

    if (data.password !== data.confirmPassword) {
      ctx.addIssue({
        path: ["confirmPassword"],
        message: "Passwords do not match",
        code: z.ZodIssueCode.custom,
      });
    }
  });

const steps = [
  {
    title: "Informations de l'école",
    icon: (
      <svg
        className="w-4 h-4 text-white lg:w-6 lg:h-6 dark:text-blue-300"
        xmlns="http://www.w3.org/2000/svg"
        fill="currentColor"
        viewBox="0 0 20 16"
      >
        <path d="M18 0H2a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2ZM6.5 3a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3.014 13.021l.157-.625A3.427 3.427 0 0 1 6.5 9.571a3.426 3.426 0 0 1 3.322 2.805l.159.622-6.967.023ZM16 12h-3a1 1 0 0 1 0-2h3a1 1 0 0 1 0 2Zm0-3h-3a1 1 0 1 1 0-2h3a1 1 0 1 1 0 2Zm0-3h-3a1 1 0 1 1 0-2h3a1 1 0 1 1 0 2Z" />
      </svg>
    ),
  },
  {
    title: "Adresse",
    icon: (
      <svg
        className="w-4 h-4 text-white lg:w-6 lg:h-6 dark:text-blue-300"
        xmlns="http://www.w3.org/2000/svg"
        fill="currentColor"
        viewBox="0 0 20 14"
      >
        <path d="M18 0H2a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2ZM2 12V6h16v6H2Z" />
        <path d="M6 8H4a1 1 0 0 0 0 2h2a1 1 0 0 0 0-2Zm8 0H9a1 1 0 0 0 0 2h5a1 1 0 1 0 0-2Z" />
      </svg>
    ),
  },
];

export const Stepper = ({ currentStep }) => {
  return (
    <div className="w-full flex justify-center">
      <ol className="flex items-center justify-between w-full max-w-[400] px-4">
        {steps.map((step, index) => (
          <li
            key={index}
            className={`flex w-full items-center ${index !== steps.length - 1
              ? 'after:content-[""] after:w-full after:h-1 after:border-b after:border-4 after:inline-block ' +
              (index < currentStep
                ? "after:border-green-400 dark:after:border-blue-800"
                : "after:border-gray-100 dark:after:border-gray-700")
              : ""
              } ${index <= currentStep ? "text-blue-600 dark:text-blue-500" : ""
              }`}
          >
            <div
              className={`flex items-center justify-center w-10 h-10 rounded-full lg:h-12 lg:w-12 shrink-0 ${index <= currentStep
                ? "bg-green-400 dark:bg-blue-800"
                : "bg-gray-100 dark:bg-gray-700"
                }`}
            >
              {step.icon}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
};

export const CLIENT_SIGNUP = gql`
  mutation ClientSignup(
    $username: String!
    $password: String!
    $accountType: String!
    $clientName: String!
    $email: String!
    $contactPerson: String!
    $contactNumber: String!
    $address: String!
    $city: String!
    $state: Int!
    $country: Int!
    $solution: String!
    $billingMode: Int!
    $clientType: String!
    $timezone: String!
    $partnerLoginId: Int
    $planId: Int
    $comments: String
    $domainBusinessId: Int
    $specificTaskId: Int
  ) {
    clientSignupCustom(
      username: $username
      password: $password
      accountType: $accountType
      clientName: $clientName
      email: $email
      contactPerson: $contactPerson
      contactNumber: $contactNumber
      address: $address
      city: $city
      state: $state
      country: $country
      solution: $solution
      billingMode: $billingMode
      clientType: $clientType
      timezone: $timezone
      partnerLoginId: $partnerLoginId
      planId: $planId
      comments: $comments
      domainBusinessId: $domainBusinessId
      specificTaskId: $specificTaskId
    ) {
      loginId
    }
  }
`;
export const ADD_SUBSCRIPTION_TMP = gql`
  mutation AddSubscriptionsCustom(
    $clientLoginId: Int!
    $partnerLoginId: Int!
    $billingFrequencyId: Int!
    $GST: Float!
    $softwareQuantity: Int!
    $deviceTrackingCharges: Int!
    $deviceModelId: Int!
    $deviceQuantity: Int!
    $hardwareCharges: Int!
    $salesRemarks: String
  ) {
    addSubscriptionsCustom(
      clientLoginId: $clientLoginId
      partnerLoginId: $partnerLoginId
      billingFrequencyId: $billingFrequencyId
      GST: $GST
      softwareQuantity: $softwareQuantity
      deviceTrackingCharges: $deviceTrackingCharges
      deviceModelId: $deviceModelId
      deviceQuantity: $deviceQuantity
      hardwareCharges: $hardwareCharges
      salesRemarks: $salesRemarks
    ) {
      imeiRandom
      subscriptionId
    }
  }
`;

export const ADD_SUBSCRIPTION = gql`
  mutation AddSubscriptionsCustom(
    $clientLoginId: Int!
    $partnerLoginId: Int!
    $billingFrequencyId: Int!
    $GST: Float!
    $softwareQuantity: Int!
    $deviceTrackingCharges: Int!
    $deviceModelId: Int!
    $deviceQuantity: Int!
    $hardwareCharges: Int!
    $salesRemarks: String
    $accessoryQuantity: Int
    $accessoryCharges: Int
    $simQuantity: Int
    $simCharges: Int
  ) {
    addSubscriptionsCustom(
      clientLoginId: $clientLoginId
      partnerLoginId: $partnerLoginId
      billingFrequencyId: $billingFrequencyId
      GST: $GST
      softwareQuantity: $softwareQuantity
      deviceTrackingCharges: $deviceTrackingCharges
      deviceModelId: $deviceModelId
      deviceQuantity: $deviceQuantity
      hardwareCharges: $hardwareCharges
      salesRemarks: $salesRemarks
      accessoryQuantity: $accessoryQuantity
      accessoryCharges: $accessoryCharges
      simQuantity: $simQuantity
      simCharges: $simCharges
    ) {
      imeiRandom
      subscriptionId
    }
  }
`;

export const CLIENT_SIGNIN = gql`
  query Login($username: String!, $password: String!) {
    login(username: $username, password: $password) {
      token
      refreshToken
      login {
        loginId
        username
        accountType
        status
        lastLoginTime
      }
    }
  }
`;
export const CLIENT_DETAIL = gql`
  query ClientDetail($loginId: Int!) {
    clientDetail(loginId: $loginId) {
      id
      clientName
      contactNumber
      contactPerson
      loginId
      partnerLoginId
    }
  }
`;
export const GET_FLEET_MANAGER_BYLOGIN_ID = gql`
  query GetFleetManagerByLoginId($loginId: Int!) {
    getFleetManagerByLoginId(loginId: $loginId) {
      name
      username
      email
      contactNumber
      partnerLoginId
      isBusinessAdmin
      isGlobal
      partner {
        businessName
      }
    }
  }
`;

export const GETREGIONSBYCLIENTLOGINID = gql`
  query GetRegionsByClientLoginId($clientLoginId: Int!) {
    zones: getRegionsByClientLoginId(clientLoginId: $clientLoginId) {
      id
      name
      clientLoginId
    }
  }
`;

export const ADD_PARTNER = gql`
  mutation partnerSignup(
    $username: String!
    $password: String!
    $accountType: String!
    $businessName: String!
    $email: String!
    $contactPerson: String!
    $contactNumber: String!
    $billingMode: Int!
    $address: String!
    $city: String!
    $state: Int!
    $country: Int!
    $pincode: Int!
    $panNumber: String!
    $partnerType: String!
    $typeOfPartner: ClientPartnerType!
    $salesPersonId: Int!
    $solution: Solution!
  ) {
    partnerSignup(
      username: $username
      password: $password
      accountType: $accountType
      businessName: $businessName
      email: $email
      contactPerson: $contactPerson
      contactNumber: $contactNumber
      billingMode: $billingMode
      address: $address
      city: $city
      state: $state
      country: $country
      pincode: $pincode
      panNumber: $panNumber
      partnerType: $partnerType
      typeOfPartner: $typeOfPartner
      salesPersonId: $salesPersonId
      solution: $solution
    )
  }
`;

export const CREATE_FLEET_MANAGER = gql`
  mutation createFleetManager(
    $name: String!
    $username: String!
    $password: String
    $email: String
    $contactNumber: String!
    $clientLoginId: Int!
    $partnerLoginId: Int
    $regionId: Int
    $regionIds: [Int!]
    $status: Int
    $isGlobal: Boolean
  ) {
    createFleetManager(
      name: $name
      username: $username
      password: $password
      email: $email
      contactNumber: $contactNumber
      clientLoginId: $clientLoginId
      partnerLoginId: $partnerLoginId
      regionId: $regionId
      regionIds: $regionIds
      status: $status
      isGlobal: $isGlobal
    )
  }
`;
export const UPDATE_FLEET_MANAGER = gql`
  mutation updateFleetManager(
    $id: Int!
    $name: String!
    $username: String!
    $email: String
    $contactNumber: String!
    $clientLoginId: Int!
    $partnerLoginId: Int
    $regionId: Int
    $regionIds: [Int!]
    $status: Int
    $isGlobal: Boolean
  ) {
    updateFleetManager(
      id: $id
      name: $name
      username: $username
      email: $email
      contactNumber: $contactNumber
      clientLoginId: $clientLoginId
      partnerLoginId: $partnerLoginId
      regionId: $regionId
      regionIds: $regionIds
      status: $status
      isGlobal: $isGlobal
    )
  }
`;

export const GET_ALL_DRIVER_VEHICLES = gql`
  query GetAllDriverVehicles($contactNumber: String!) {
    getAllDriverVehicles(contactNumber: $contactNumber) {
      driverName
      rfid
      associatedVehicles {
        vehicleId
        vehicleNumber
      }
    }
  }
`;

export const GET_ALL_PARTNER_SUBSCRIPTION = gql`
  query ($partnerLoginId: Int!, $deviceModelId: Int) {
    getAllPartnerSubscriptions(
      partnerLoginId: $partnerLoginId
      deviceModelId: $deviceModelId
    ) {
      id
      deviceTrackingCharges
      totalSoftwareQty
      __typename
    }
  }
`;

export const GET_ALL_PLANS = gql`
query{
  getAllPlans(partnerLoginId:${partnerLoginId}){
    planName
      id
      description
      numberOfAssociatedClient
      costPerAsset
      partnerLoginId
      featureList{
        id
        featureName
        featureDescription
        featureKey
      }
  }
}`;

export const GET_ALL_ASSIGNED_SIM_TO_CLIENT = gql`
  query ($clientLoginId: Int!) {
    getAllAssignedSimToClient(clientLoginId: $clientLoginId) {
      id
      simId
      sim {
        simNumber
        phoneNumber
      }

      serviceProvider {
        name
      }
    }
  }
`;
export const GET_ALL_SERVICE_PROVIDERS = gql`
  query {
    allServiceProviders {
      id
      name
    }
  }
`;
export const GET_ALL_DEVICE_MODELS = gql`
  query {
    allDeviceModels {
      model_name
      devicetype
      id
    }
  }
`;

export const GET_ALL_ASSIGNED_DIVICES_TO_CLIENT = gql`
  query ($clientLoginId: Int!) {
    getAllAssignedDevicesToClient(clientLoginId: $clientLoginId) {
      uniqueDeviceId
      id
      device {
        imei_num
        serial_num
      }
      deviceModel {
        model_name
        devicetype
      }
    }
  }
`;
export const GET_ALL_DRIVER_LIMIT = gql`
  query ($clientLoginId: Int!) {
    getAllDriverClientLoginId(clientLoginId: $clientLoginId) {
      driverName
      contactNumber
      driverImage
      id
      license
      rfid
    }
  }
`;

export const ADD_DRIVER = gql`
  mutation (
    $driverName: String!
    $license: String!
    $contactNumber: String!
    $userLoginId: Int
    $clientLoginId: Int
    $vehicleId: Int
    $driverImage: String
    $otherDocument: String
    $rfid: String
    $vehicleIds: [Int]!
    $code: String
    $street: String
    $city: String
    $cni: String
    $supervisor: String
    $pendingrfid: String
    $exclusive: Boolean
  ) {
    addDriver(
      driverName: $driverName
      license: $license
      contactNumber: $contactNumber
      userLoginId: $userLoginId
      clientLoginId: $clientLoginId
      vehicleId: $vehicleId
      driverImage: $driverImage
      otherDocument: $otherDocument
      rfid: $rfid
      vehicleIds: $vehicleIds
      code: $code
      street: $street
      city: $city
      cni: $cni
      supervisor: $supervisor
      pendingrfid: $pendingrfid
      exclusive: $exclusive
    )
  }
`;
export const GET_VEHICLE_DETAILS = gql`
  query GetVehicleDetail($entityId: Int!) {
    getVehicleDetail(entityId: $entityId) {
      entityId
      vehicleNumber
      engineNumber
      averageMileage
      chassisNumber
      client {
        clientName
        city
      }
      device {
        uniqueDeviceId
        serial_num
        deviceModelId {
          model_name
          devicetype
        }
      }
      sim {
        phoneNumber
        simNumber
        serviceProvider {
          name
          id
        }
      }
      speedSensorType
      speedLimit
      vehicleType
      vehicleModel
      vehicleCapacity
      status
      certificatePath
      approveStatus
      idlingSpeed
      vehicleLicense
      cartegrise
      amortization
      insurance
      purchaseDate
      isInGarage
      isInWhiteList
      garageDate
      whiteListDate
      regionId
      isOBDCanFuel
      obdCanFuelDate
      whiteListEndDate
      garageEndDate
      obdCanFuelEndDate
      isBadging
      responsibleId
    }
  }
`;

export const UPDATE_DRIVER = gql`
  mutation UpdateDriver(
    $id: Int!
    $driverName: String!
    $license: String!
    $contactNumber: String!
    $clientLoginId: Int
    $userLoginId: Int
    $vehicleId: Int
    $driverImage: String
    $otherDocument: String
    $rfid: String
    $status: Int!
    $vehicleIds: [Int]
    $code: String
    $street: String
    $city: String
    $cni: String
    $supervisor: String
    $pendingrfid: String
    $exclusive: Boolean
  ) {
    updateDriverDetail(
      id: $id
      driverName: $driverName
      license: $license
      contactNumber: $contactNumber
      clientLoginId: $clientLoginId
      userLoginId: $userLoginId
      vehicleId: $vehicleId
      driverImage: $driverImage
      otherDocument: $otherDocument
      rfid: $rfid
      status: $status
      vehicleIds: $vehicleIds
      code: $code
      street: $street
      city: $city
      cni: $cni
      supervisor: $supervisor
      pendingrfid: $pendingrfid
      exclusive: $exclusive
    )
  }
`;

export const ADD_VEHICLE_CUSTOM = gql`
  mutation AddVehicleCustom(
    $vehicleNumber: String!
    $engineNumber: String
    $chassisNumber: String
    $clientLoginId: Int!
    $deviceModelId: Int!
    $uniqueDeviceId: String!
    $simNumber: String!
    $serviceProviderId: Int!
    $manufacturerId: Int
    $phoneNumber: String!
    $speedSensorType: String!
    $speedLimit: Int!
    $uniqueAccessoryId: String
    $vehicleType: String!
    $vehicleMake: String
    $averageMileage: Int
    $vehicleModel: String!
    $vehicleCapacity: Int
    $isActivated: Boolean
    $partnerSubscriptionId: Int
    $accountType: String
    $vehicleIdentificationNumber: String
    $vehicleColour: String
    $vehicleLicense: String
    $cartegrise: String
    $amortization: Int
    $insurance: String
    $purchaseDate: String
    $isInWhiteList: Boolean
    $isInGarage: Boolean
    $isBadging: Boolean
    $whiteListDate: Float
    $garageDate: Float
    $regionId: Int
    $serialNum: String!
    $isOBDCanFuel: Boolean
    $obdCanFuelDate: Float
  ) {
    addVehicleCustom(
      vehicleNumber: $vehicleNumber
      engineNumber: $engineNumber
      chassisNumber: $chassisNumber
      clientLoginId: $clientLoginId
      deviceModelId: $deviceModelId
      uniqueDeviceId: $uniqueDeviceId
      simNumber: $simNumber
      serviceProviderId: $serviceProviderId
      manufacturerId: $manufacturerId
      phoneNumber: $phoneNumber
      speedSensorType: $speedSensorType
      speedLimit: $speedLimit
      uniqueAccessoryId: $uniqueAccessoryId
      vehicleType: $vehicleType
      vehicleMake: $vehicleMake
      averageMileage: $averageMileage
      vehicleModel: $vehicleModel
      vehicleCapacity: $vehicleCapacity
      isActivated: $isActivated
      partnerSubscriptionId: $partnerSubscriptionId
      accountType: $accountType
      vehicleIdentificationNumber: $vehicleIdentificationNumber
      vehicleColour: $vehicleColour
      vehicleLicense: $vehicleLicense
      cartegrise: $cartegrise
      amortization: $amortization
      insurance: $insurance
      purchaseDate: $purchaseDate
      isInWhiteList: $isInWhiteList
      isInGarage: $isInGarage
      whiteListDate: $whiteListDate
      garageDate: $garageDate
      regionId: $regionId
      serialNum: $serialNum
      isOBDCanFuel: $isOBDCanFuel
      obdCanFuelDate: $obdCanFuelDate
      isBadging: $isBadging
    )
  }
`;

export const SEND_COMMAND_TRACKER = gql`
  mutation sendCommandTracker($imei: String!, $command: String!) {
    sendCommandTracker(imei: $imei, command: $command) {
      success
      message
    }
  }
`;

export const GET_DEVICE_COMANDES_MONITORING = gql`
  query getDeviceCommandsMonitoring(
    $id: Int
    $uniqueid: String
    $status: String
    $username: String
    $clientLoginId: Int
    $userId: Int
    $vehicleNumber: String
  ) {
   items: getDeviceCommandsMonitoring(
      id: $id
      uniqueid: $uniqueid
      status: $status
      username: $username
      clientLoginId: $clientLoginId
      userId: $userId
      vehicleNumber: $vehicleNumber
    ) {
      id
      uniqueid
      command
      status
      create_ts
      modified_ts
      response
      commandDisplay
      username
      clientLoginId
      userId
      vehicleNumber
    }
  }
`;


export const SEND_COMMAND_TRACKER_V2 = gql`
  mutation sendCommandTrackerV2(
    $imei: String!
    $command: String!
    $typeCommand: String!
    $clientLoginId: Int
    $username: String
    $userId: Int!
    $vehicleNumber: String
  ) {
    sendCommandTrackerV2(
      imei: $imei
      command: $command
      typeCommand: $typeCommand
      clientLoginId: $clientLoginId
      username: $username
      userId: $userId
      vehicleNumber: $vehicleNumber
    ) {
      success
      message
    }
  }
`;


export const DECOUPLE_DEVICE_SIM = gql`
  mutation DecoupleDeviceSimCustom(
    $newDeviceUniqueId: String
    $existingDeviceUniqueId: String
    $newDeviceModelId: Int
    $newSerialNum: String
    $newSimNumber: String
    $newServiceProviderId: Int
    $newPhoneNumber: String
    $existingSimNumber: String
    $decoupleType: String!
    $loginType: String!
    $clientLoginId: Int!
    $manufacturerId: Int
  ) {
    decoupleDeviceSimCustom(
      newDeviceUniqueId: $newDeviceUniqueId
      existingDeviceUniqueId: $existingDeviceUniqueId
      newDeviceModelId: $newDeviceModelId
      newSerialNum: $newSerialNum
      newSimNumber: $newSimNumber
      newServiceProviderId: $newServiceProviderId
      newPhoneNumber: $newPhoneNumber
      existingSimNumber: $existingSimNumber
      decoupleType: $decoupleType
      loginType: $loginType
      clientLoginId: $clientLoginId
      manufacturerId: $manufacturerId
    )
  }
`;

export const ADD_VEHICLE_DETAIL = gql`
  mutation AddVehicleDetail(
    $vehicleNumber: String!
    $engineNumber: String
    $chassisNumber: String
    $clientLoginId: Int!
    $uniqueDeviceId: String!
    $simId: Int!
    $speedSensorType: String!
    $speedLimit: Int!
    $uniqueAccessoryId: String
    $vehicleType: String!
    $vehicleMake: String
    $averageMileage: Int
    $vehicleModel: String!
    $vehicleCapacity: Int
    $isActivated: Boolean
    $partnerSubscriptionId: Int!
    $accountType: String
    $vehicleIdentificationNumber: String
    $vehicleColour: String
    $vehicleLicense: String
    $cartegrise: String
    $amortization: Int
    $insurance: String
    $purchaseDate: String
    $isInWhiteList: Boolean
    $isInGarage: Boolean
    $whiteListDate: Float
    $garageDate: Float
    $regionId: Int
  ) {
    addVehicleDetail(
      vehicleNumber: $vehicleNumber
      engineNumber: $engineNumber
      chassisNumber: $chassisNumber
      clientLoginId: $clientLoginId
      uniqueDeviceId: $uniqueDeviceId
      simId: $simId
      speedSensorType: $speedSensorType
      speedLimit: $speedLimit
      uniqueAccessoryId: $uniqueAccessoryId
      vehicleType: $vehicleType
      vehicleMake: $vehicleMake
      averageMileage: $averageMileage
      vehicleModel: $vehicleModel
      vehicleCapacity: $vehicleCapacity
      isActivated: $isActivated
      partnerSubscriptionId: $partnerSubscriptionId
      accountType: $accountType
      vehicleIdentificationNumber: $vehicleIdentificationNumber
      vehicleColour: $vehicleColour
      vehicleLicense: $vehicleLicense
      cartegrise: $cartegrise
      amortization: $amortization
      insurance: $insurance
      purchaseDate: $purchaseDate
      isInWhiteList: $isInWhiteList
      isInGarage: $isInGarage
      whiteListDate: $whiteListDate
      garageDate: $garageDate
      regionId: $regionId
    )
  }
`;
export const UPDATE_VEHICLE_DETAIL = gql`
  mutation UpdateVehicleDetail(
    $entityId: Int!
    $vehicleNumber: String!
    $engineNumber: String
    $chassisNumber: String
    $clientLoginId: Int!
    $speedSensorType: String!
    $speedLimit: Int!
    $vehicleType: String!
    $vehicleModel: String!
    $vehicleCapacity: Int
    $isActivated: Boolean
    $status: Int!
    $vehicleLicense: String
    $cartegrise: String
    $amortization: Int
    $insurance: String
    $purchaseDate: String
    $isInWhiteList: Boolean
    $isInGarage: Boolean
    $whiteListDate: Float
    $garageDate: Float
    $regionId: Int
    $isOBDCanFuel: Boolean
    $obdCanFuelDate: Float
    $whiteListEndDate: Float
    $garageEndDate: Float
    $obdCanFuelEndDate: Float
    $isBadging: Boolean
  ) {
    updateVehicleDetail(
      entityId: $entityId
      vehicleNumber: $vehicleNumber
      engineNumber: $engineNumber
      chassisNumber: $chassisNumber
      clientLoginId: $clientLoginId
      speedSensorType: $speedSensorType
      speedLimit: $speedLimit
      vehicleType: $vehicleType
      vehicleModel: $vehicleModel
      vehicleCapacity: $vehicleCapacity
      isActivated: $isActivated
      status: $status
      vehicleLicense: $vehicleLicense
      cartegrise: $cartegrise
      amortization: $amortization
      insurance: $insurance
      purchaseDate: $purchaseDate
      isInWhiteList: $isInWhiteList
      isInGarage: $isInGarage
      whiteListDate: $whiteListDate
      garageDate: $garageDate
      regionId: $regionId
      isOBDCanFuel: $isOBDCanFuel
      obdCanFuelDate: $obdCanFuelDate
      whiteListEndDate: $whiteListEndDate
      garageEndDate: $garageEndDate
      obdCanFuelEndDate: $obdCanFuelEndDate
      isBadging: $isBadging
    )
  }
`;
